import React, { useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardHeader, Modal, Collapse, Button, Input, Label, Table, Progress,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import moment from 'moment';

import { post } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import PickrouteGenerator, {
  pickrouteSimulationTypeCodes,
  pickrouteTypeCodes,
} from '../../components/Warehouse/PickrouteGenerator';
import WarehouseMapCanvas from '../../components/Warehouse/WarehouseMapCanvas';

function Warehouse(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  // eslint-disable-next-line no-unused-vars
  const { t } = props;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(yesterday);
  const [slideDurationMicroseconds, setSlideDurationMicroseconds] = useState(1000000);
  const [animationWeekday, setAnimationWeekday] = useState('');
  const [heatmapRouteType, setHeatmapRouteType] = useState('');

  const launchCreate = () => {
    setCreateModalOpen(true);
  };

  const deleteRoute = async (route) => {
    try {
      await post(`/api/warehouse/pick/delete/${route.id}`);
      toastr.success(props.t('Pick Route deleted'));
      setRefresh(refresh + 1);
    } catch (err) {
      toastr.error(props.t('Could not delete Pick Route'));
    }
  };

  const runSimulation = async () => {
    setSimulationRunning(true);
    try {
      const result = await post('/api/warehouse/pick/simulation', { types: pickrouteSimulationTypeCodes });
      setSimulation(result.routes);
      setCreateModalOpen(false);
    } catch (error) {
      toastr.error(error.message || props.t('Could not run pick route simulation'));
    } finally {
      setSimulationRunning(false);
    }
  };

  const simulationLimits = {
    'Single-Part': { metric: 'orders', max: 30 },
    'Dims Class A': { metric: 'orders', max: 30 },
    'Dims Class A Fedex': { metric: 'orders', max: 30 },
    'Multi-Part': { metric: 'orders', max: 6 },
    Shark: { metric: 'stops', max: 100 },
    'LED Strips': { metric: 'stops', max: 40 },
    'Store Pick-Up': { metric: 'orders', max: 6 },
    'Priority Orders': { metric: 'orders', max: 6 },
    FedEx: { metric: 'orders', max: 30 },
    'Squaretrade Only': { metric: 'orders', max: 30 },
    ME: { metric: 'orders', max: 6 },
    'Next Day ONLY': { metric: 'orders', max: 6 },
    'Scrap Orders': { metric: 'orders', max: 10 },
  };

  const getSimulationProgress = (type, totals) => {
    const limit = simulationLimits[type];
    if (!limit) return null;

    const value = Number(totals[limit.metric] || 0);
    const percent = Math.min(100, Math.round((value / limit.max) * 100));

    return {
      ...limit,
      value,
      percent,
    };
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader className="bg-transparent border-bottom">{props.t('Pick Routes')}</CardHeader>
              <CardBody>
                <Can i="see" this="warehouse">
                  {simulation && (
                    <Row>
                      <Col xl="8">
                        <Card className="border mb-3">
                          <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>{props.t('Fulfillment Route Simulation')}</strong>
                              <Button color="link" size="sm" className="p-0" onClick={() => setSimulation(null)}>{props.t('Close')}</Button>
                            </div>
                            <Table responsive size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  <th>{props.t('Route Type')}</th>
                                  <th className="text-end">{props.t('Orders')}</th>
                                  <th className="text-end">{props.t('Items')}</th>
                                  <th className="text-end">{props.t('Stops')}</th>
                                  <th style={{ minWidth: '160px' }}>{props.t('Capacity')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(simulation).map(([type, totals]) => {
                                  const progress = getSimulationProgress(type, totals);

                                  return (
                                    <tr key={type}>
                                      <td>{props.t(type)}</td>
                                      <td className="text-end">{totals.orders}</td>
                                      <td className="text-end">{totals.items}</td>
                                      <td className="text-end">{totals.stops}</td>
                                      <td>
                                        {progress ? (
                                          <>
                                            <div className="d-flex justify-content-between small text-muted mb-1">
                                              <span>{props.t(progress.metric)}</span>
                                              <span>{`${progress.value}/${progress.max}`}</span>
                                            </div>
                                            <Progress
                                              value={progress.percent}
                                              color={progress.percent >= 100 ? 'success' : 'primary'}
                                              style={{ height: '6px' }}
                                            />
                                          </>
                                        ) : (
                                          <span className="text-muted">{props.t('No limit')}</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                            <small className="text-muted d-block mt-2">{props.t('Simulation changes are rolled back and do not create pick routes.')}</small>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}
                  <div className="mb-3">
                    <div className="d-flex justify-content-end align-items-center mb-2">
                      <Button
                        color="link"
                        className="p-0 text-decoration-none"
                        onClick={() => { setMapOpen((open) => !open); }}
                      >
                        {mapOpen ? props.t('Hide Map') : props.t('Show Map')}
                      </Button>
                    </div>
                    <Collapse isOpen={mapOpen}>
                      <div className="mb-3">
                        <Row className="g-3 mb-3">
                          <Col md="6" xl>
                            <Label for="pickroute-heatmap-start" className="form-label">{props.t('Start Date')}</Label>
                            <Input
                              id="pickroute-heatmap-start"
                              type="date"
                              value={startDate}
                              onChange={(event) => { setStartDate(event.target.value); }}
                            />
                          </Col>
                          <Col md="6" xl>
                            <Label for="pickroute-heatmap-end" className="form-label">{props.t('End Date')}</Label>
                            <Input
                              id="pickroute-heatmap-end"
                              type="date"
                              value={endDate}
                              onChange={(event) => { setEndDate(event.target.value); }}
                            />
                          </Col>
                          <Col md="6" xl>
                            <Label for="pickroute-heatmap-duration" className="form-label">{props.t('Slide Duration (microseconds)')}</Label>
                            <Input
                              id="pickroute-heatmap-duration"
                              type="number"
                              min="1000"
                              step="1000"
                              value={slideDurationMicroseconds}
                              onChange={(event) => {
                                setSlideDurationMicroseconds(
                                  Math.max(1000, Number(event.target.value) || 1000),
                                );
                              }}
                            />
                            <small className="text-muted">{props.t('1,000,000 µs = 1 second')}</small>
                          </Col>
                          <Col md="6" xl>
                            <Label for="pickroute-heatmap-weekday" className="form-label">{props.t('Animation Days')}</Label>
                            <Input
                              id="pickroute-heatmap-weekday"
                              type="select"
                              value={animationWeekday}
                              onChange={(event) => { setAnimationWeekday(event.target.value); }}
                            >
                              <option value="">{props.t('All weekdays')}</option>
                              <option value="1">{props.t('Mondays')}</option>
                              <option value="2">{props.t('Tuesdays')}</option>
                              <option value="3">{props.t('Wednesdays')}</option>
                              <option value="4">{props.t('Thursdays')}</option>
                              <option value="5">{props.t('Fridays')}</option>
                            </Input>
                            <small className="text-muted">{props.t('Saturday and Sunday are skipped')}</small>
                          </Col>
                          <Col md="6" xl>
                            <Label for="pickroute-heatmap-route-type" className="form-label">{props.t('Pick Route Type')}</Label>
                            <Input
                              id="pickroute-heatmap-route-type"
                              type="select"
                              value={heatmapRouteType}
                              onChange={(event) => { setHeatmapRouteType(event.target.value); }}
                            >
                              <option value="">{props.t('All route types')}</option>
                              {pickrouteTypeCodes.map((type) => (
                                <option key={type} value={type}>{props.t(type)}</option>
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <WarehouseMapCanvas
                          layoutModeType={props.layoutModeType}
                          mode="heatmap"
                          routeType={heatmapRouteType}
                          animationWeekday={animationWeekday}
                          slideDurationMicroseconds={slideDurationMicroseconds}
                          startDate={startDate}
                          endDate={endDate}
                          title={props.t('Warehouse Map')}
                        />
                      </div>
                    </Collapse>
                  </div>
                  <BaseGrid
                    url="/api/warehouse/pick"
                    hasSearch
                    create={launchCreate}
                    createText={props.t('Generate')}
                    shouldUpdate={refresh}
                    config={
                    {
                      columns: {
                        id: {
                          label: 'ID',
                          sort: true,
                          link: {
                            to: '/warehouse/pickroute/%id%',
                          },
                        },
                        ready: {
                          label: 'Process',
                          style: { fontSize: '12px' },
                          html: true,
                          format: (y, x) => {
                            if (x.finished_at !== null) {
                              return '<span class="badge bg-success"><span class="mdi mdi-check-bold"></span></span>';
                            }

                            if (y && x.items.length > 0) {
                              return `<span class="badge bg-primary">${t('Ready!')}</span>`;
                            }

                            if (y && x.items.length === 0) {
                              return `<span class="badge bg-dark">${t('Scrubbed')}</span>`;
                            }

                            return `<span class="badge bg-dark text-white bg-soft">${t('Queued')}</span>`;
                          },
                        },
                        user_name: { label: 'Generated By' },
                        type: { label: 'Type' },
                        stops: { label: 'Stops', format: (y, x) => x.items.length },
                        items: { label: 'Items', format: (x) => x.map((z) => z.qty).reduce((a, c) => (a + c), 0) },
                        picked: { label: 'Picked', format: (y, x) => x.items.map((z) => z.picked_qty + (z.void * z.qty)).reduce((a, c) => (a + c), 0) },
                        created_at: {
                          label: 'Created At',
                          date: {
                            format: (date) => (moment(date).fromNow()),
                            timezone: process.env.REACT_APP_LOCAL_TZ,
                          },
                          sort: true,
                        },
                        finished_at: {
                          label: 'Finished At',
                          date: {
                            format: (date) => (moment(date).fromNow()),
                            timezone: process.env.REACT_APP_LOCAL_TZ,
                          },
                          sort: true,
                        },
                        progress: {
                          label: 'Progress',
                          progress: {
                            value: (x) => (x.items.map(
                              (z) => z.picked_qty + (z.void * z.qty),
                            ).reduce((a, c) => (a + c), 0) / x.items.map(
                              (z) => z.qty,
                            ).reduce((a, c) => (a + c), 0)) * 100,
                            color: (x) => (x.finished_at !== null ? 'success' : 'primary'),
                          },
                        },
                        actions: {
                          label: 'Actions',
                          actions: [
                            {
                              key: 'delete',
                              icon: 'mdi mdi-close-thick',
                              class: 'btn btn-sm btn-soft-danger',
                              alt: props.t('Delete'),
                              click: deleteRoute,
                              condition: (x) => (x.items.length === 0),
                            },
                          ],
                        },
                      },
                      pagination: {
                        sort: 'id',
                        direction: 'desc',
                        perPage: 30,
                      },
                    }
                    }
                  />
                </Can>

                <Can not i="see" this="warehouse"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>

            <Modal
              size="md"
              isOpen={createModalOpen}
            >
              <div className="modal-header">
                <h5
                  className="modal-title mt-0"
                >
                  {props.t('Generate Pick Route')}
                </h5>
                <button
                  onClick={() => { setCreateModalOpen(false); }}
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <PickrouteGenerator onSuccess={() => {
                  setCreateModalOpen(false);
                  setRefresh(refresh + 1);
                }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setCreateModalOpen(false); }}
                  className="btn btn-soft-dark"
                  data-dismiss="modal"
                >
                  {props.t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={runSimulation}
                  className="btn btn-soft-primary"
                  disabled={simulationRunning}
                >
                  {simulationRunning ? props.t('Running Simulation...') : props.t('Run Simulation')}
                </button>
                <button
                  type="submit"
                  form="GeneratePickrouteForm"
                  className="btn btn-primary"
                >
                  {props.t('Generate')}
                </button>
              </div>
            </Modal>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Warehouse.propTypes = {
  layoutModeType: PropTypes.string,
  t: PropTypes.any,
};

Warehouse.defaultProps = {
  layoutModeType: 'light',
  t: (x) => (x),
};

export default connect((state) => ({
  layoutModeType: state.Layout.layoutModeType,

}), {})(withTranslation()(Warehouse));
